import { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  ArrowLeft, Shield, Users, Key, Clock, Search, Plus, Pencil, Trash2,
  Eye, History, RefreshCw, UserPlus, CheckSquare, Square, Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import LanguageSwitcher from '../components/LanguageSwitcher';

function AdminSecurity({ user, onLogout }) {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'ru';
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('roles');
  const [loading, setLoading] = useState(true);
  
  // Roles data
  const [roles, setRoles] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({
    name: '',
    description: '',
    permissions: []
  });
  
  // Audit log data
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditFilters, setAuditFilters] = useState({
    action: 'all',
    resource_type: 'all',
    date_from: '',
    date_to: ''
  });

  // Security threat logs
  const [threatStats, setThreatStats] = useState(null);
  const [threatLogs, setThreatLogs] = useState([]);
  const [threatPage, setThreatPage] = useState(1);
  const [threatTotal, setThreatTotal] = useState(0);
  const [threatFilter, setThreatFilter] = useState('all');
  const [threatDays, setThreatDays] = useState(7);
  const [threatLoading, setThreatLoading] = useState(false);
  const [auditFilterOptions, setAuditFilterOptions] = useState({
    actions: [],
    resource_types: []
  });
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditTotalPages, setAuditTotalPages] = useState(1);
  
  // Admin users
  const [adminUsers, setAdminUsers] = useState([]);
  const [showAdminDialog, setShowAdminDialog] = useState(false);
  const [newAdmin, setNewAdmin] = useState({
    email: '',
    password: '',
    full_name: '',
    role_id: ''
  });

  const PERMISSION_GROUPS = {
    'Заказы': ['orders.view', 'orders.create', 'orders.edit', 'orders.delete'],
    'Товары': ['products.view', 'products.create', 'products.edit', 'products.delete'],
    'Клиенты': ['clients.view', 'clients.create', 'clients.edit', 'clients.delete'],
    'Платежи': ['payments.view', 'payments.create', 'payments.edit'],
    'Поддержка': ['support.view', 'support.reply', 'support.manage'],
    'Доставка': ['delivery.view', 'delivery.assign', 'delivery.manage', 'delivery.routes'],
    'Аналитика': ['analytics.view', 'reports.export'],
    'Настройки': ['settings.view', 'settings.edit'],
    'Пользователи': ['users.view', 'users.create', 'users.edit', 'users.delete'],
    'Интеграции': ['integrations.view', 'integrations.manage']
  };

  const PERMISSION_LABELS = {
    'orders.view': 'Просмотр',
    'orders.create': 'Создание',
    'orders.edit': 'Редактирование',
    'orders.delete': 'Удаление',
    'products.view': 'Просмотр',
    'products.create': 'Создание',
    'products.edit': 'Редактирование',
    'products.delete': 'Удаление',
    'clients.view': 'Просмотр',
    'clients.create': 'Создание',
    'clients.edit': 'Редактирование',
    'clients.delete': 'Удаление',
    'payments.view': 'Просмотр',
    'payments.create': 'Создание',
    'payments.edit': 'Редактирование',
    'support.view': 'Просмотр',
    'support.reply': 'Ответы',
    'support.manage': 'Управление',
    'delivery.view': 'Просмотр',
    'delivery.assign': 'Назначение',
    'delivery.manage': 'Управление',
    'delivery.routes': 'Создание маршрутов',
    'analytics.view': 'Просмотр',
    'reports.export': 'Экспорт',
    'settings.view': 'Просмотр',
    'settings.edit': 'Редактирование',
    'users.view': 'Просмотр',
    'users.create': 'Создание',
    'users.edit': 'Редактирование',
    'users.delete': 'Удаление',
    'integrations.view': 'Просмотр',
    'integrations.manage': 'Управление'
  };

  useEffect(() => {
    fetchRoles();
    fetchAdminUsers();
  }, []);

  useEffect(() => {
    if (activeTab === 'audit') {
      fetchAuditLogs();
    }
    if (activeTab === 'threats') {
      fetchThreatStats();
      fetchThreatLogs();
    }
  }, [activeTab, auditPage, auditFilters, threatPage, threatFilter, threatDays]);

  const fetchThreatStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/security/stats?days=${threatDays}`, { headers: getAuthHeaders() });
      setThreatStats(response.data);
    } catch (err) {
      console.error('Failed to fetch threat stats:', err);
    }
  };

  const fetchThreatLogs = async () => {
    setThreatLoading(true);
    try {
      let url = `${API}/admin/security/logs?page=${threatPage}&days=${threatDays}&limit=30`;
      if (threatFilter !== 'all') url += `&event_type=${threatFilter}`;
      const response = await axios.get(url, { headers: getAuthHeaders() });
      setThreatLogs(response.data.logs);
      setThreatTotal(response.data.total);
    } catch (err) {
      console.error('Failed to fetch threat logs:', err);
    }
    setThreatLoading(false);
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/admin/roles`, { headers: getAuthHeaders() });
      setRoles(response.data.roles);
      setAvailablePermissions(response.data.available_permissions);
    } catch (error) {
      toast.error('Не удалось загрузить роли');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/admin-users`, { headers: getAuthHeaders() });
      setAdminUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch admin users');
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const params = new URLSearchParams({
        page: auditPage.toString(),
        limit: '30',
        ...(auditFilters.action !== 'all' && { action: auditFilters.action }),
        ...(auditFilters.resource_type !== 'all' && { resource_type: auditFilters.resource_type }),
        ...(auditFilters.date_from && { date_from: auditFilters.date_from }),
        ...(auditFilters.date_to && { date_to: auditFilters.date_to }),
      });
      
      const response = await axios.get(`${API}/admin/audit-log?${params}`, {
        headers: getAuthHeaders()
      });
      
      setAuditLogs(response.data.logs);
      setAuditTotal(response.data.total);
      setAuditTotalPages(response.data.total_pages);
      setAuditFilterOptions({
        actions: response.data.filters.actions,
        resource_types: response.data.filters.resource_types
      });
    } catch (error) {
      toast.error('Не удалось загрузить журнал');
    }
  };

  const handleSaveRole = async (e) => {
    e.preventDefault();
    if (!roleForm.name) {
      toast.error('Укажите название роли');
      return;
    }
    
    try {
      if (editingRole) {
        await axios.put(`${API}/admin/roles/${editingRole.id}`, roleForm, {
          headers: getAuthHeaders()
        });
        toast.success('Роль обновлена');
      } else {
        await axios.post(`${API}/admin/roles`, roleForm, {
          headers: getAuthHeaders()
        });
        toast.success('Роль создана');
      }
      setShowRoleDialog(false);
      resetRoleForm();
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Не удалось сохранить роль');
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!confirm('Удалить роль?')) return;
    
    try {
      await axios.delete(`${API}/admin/roles/${roleId}`, { headers: getAuthHeaders() });
      toast.success('Роль удалена');
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Не удалось удалить роль');
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    if (!newAdmin.email || !newAdmin.password || !newAdmin.full_name) {
      toast.error('Заполните обязательные поля');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/admin-users`, {
        email: newAdmin.email,
        password: newAdmin.password,
        full_name: newAdmin.full_name,
        ...(newAdmin.role_id && { role_id: newAdmin.role_id })
      }, {
        headers: getAuthHeaders()
      });
      
      toast.success('Администратор создан');
      setShowAdminDialog(false);
      setNewAdmin({ email: '', password: '', full_name: '', role_id: '' });
      fetchAdminUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Не удалось создать администратора');
    }
  };

  const openEditRole = (role) => {
    setEditingRole(role);
    setRoleForm({
      name: role.name,
      description: role.description || '',
      permissions: role.permissions || []
    });
    setShowRoleDialog(true);
  };

  const resetRoleForm = () => {
    setEditingRole(null);
    setRoleForm({ name: '', description: '', permissions: [] });
  };

  const togglePermission = (permission) => {
    setRoleForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permission)
        ? prev.permissions.filter(p => p !== permission)
        : [...prev.permissions, permission]
    }));
  };

  const toggleGroupPermissions = (groupPermissions) => {
    const allSelected = groupPermissions.every(p => roleForm.permissions.includes(p));
    if (allSelected) {
      setRoleForm(prev => ({
        ...prev,
        permissions: prev.permissions.filter(p => !groupPermissions.includes(p))
      }));
    } else {
      setRoleForm(prev => ({
        ...prev,
        permissions: [...new Set([...prev.permissions, ...groupPermissions])]
      }));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionLabel = (action) => {
    const labels = {
      'role_created': 'Создание роли',
      'role_updated': 'Обновление роли',
      'role_deleted': 'Удаление роли',
      'user_role_assigned': 'Назначение роли',
      'admin_user_created': 'Создание админа',
      'integration_updated': 'Настройка интеграции',
      'integration_synced': 'Синхронизация',
      'notification_settings_updated': 'Настройки уведомлений',
      'b2b_company_code_updated': 'Обновление кода компании',
      'b2b_network_created': 'Создание B2B сети',
      'b2b_network_deleted': 'Удаление B2B сети',
      'b2b_manager_created': 'Создание B2B менеджера',
      'b2b_manager_deactivated': 'Деактивация B2B менеджера',
      'b2b_password_reset': 'Сброс пароля B2B',
    };
    return labels[action] || action;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
          <div className="text-lg text-gray-600">Загрузка...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-[1600px] mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to={`/${currentLang}/admin`}>
                <Button variant="ghost" size="sm" data-testid="back-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Назад
                </Button>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Безопасность</h1>
                <p className="text-sm text-gray-500">Роли, права доступа и журнал действий</p>
              </div>
            </div>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="roles" className="flex items-center gap-2">
              <Key className="w-4 h-4" />
              Роли ({roles.length})
            </TabsTrigger>
            <TabsTrigger value="admins" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Администраторы ({adminUsers.length})
            </TabsTrigger>
            <TabsTrigger value="audit" className="flex items-center gap-2">
              <History className="w-4 h-4" />
              Журнал действий
            </TabsTrigger>
            <TabsTrigger value="threats" className="flex items-center gap-2" data-testid="threats-tab">
              <Shield className="w-4 h-4" />
              Журнал угроз
            </TabsTrigger>
          </TabsList>

          {/* Roles Tab */}
          <TabsContent value="roles">
            <div className="flex justify-end mb-4">
              <Button
                onClick={() => {
                  resetRoleForm();
                  setShowRoleDialog(true);
                }}
                className="bg-emerald-700 hover:bg-emerald-800"
              >
                <Plus className="w-4 h-4 mr-2" />
                Создать роль
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {roles.map((role) => (
                <Card key={role.id} data-testid={`role-card-${role.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Shield className="w-5 h-5 text-emerald-600" />
                          {role.name}
                        </CardTitle>
                        {role.is_system && (
                          <Badge variant="outline" className="mt-1">Системная</Badge>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEditRole(role)} data-testid={`edit-role-${role.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {!role.is_system && (
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteRole(role.id)} data-testid={`delete-role-${role.id}`}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <CardDescription>{role.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm text-gray-600 mb-2">
                      Прав доступа: <strong>{role.permissions.length}</strong>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {(() => {
                        const GROUP_LABELS = {
                          'orders': 'Заказы',
                          'products': 'Товары',
                          'clients': 'Клиенты',
                          'payments': 'Платежи',
                          'support': 'Поддержка',
                          'delivery': 'Доставка',
                          'analytics': 'Аналитика',
                          'reports': 'Отчёты',
                          'settings': 'Настройки',
                          'users': 'Пользователи',
                          'integrations': 'Интеграции',
                          'content': 'Контент'
                        };
                        const groups = {};
                        (role.permissions || []).forEach(p => {
                          const prefix = p === '*' ? '*' : p.split('.')[0];
                          groups[prefix] = (groups[prefix] || 0) + 1;
                        });
                        if (groups['*']) return <Badge variant="secondary" className="text-xs">Полный доступ</Badge>;
                        return Object.entries(groups).map(([prefix, count]) => (
                          <Badge key={prefix} variant="secondary" className="text-xs">
                            {GROUP_LABELS[prefix] || prefix} ({count})
                          </Badge>
                        ));
                      })()}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Admins Tab */}
          <TabsContent value="admins">
            <div className="flex justify-end mb-4">
              <Button
                onClick={() => setShowAdminDialog(true)}
                className="bg-emerald-700 hover:bg-emerald-800"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Добавить администратора
              </Button>
            </div>

            <Card>
              <CardContent className="p-0">
                <table className="w-full" data-testid="admins-table">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Имя</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Email</th>
                      <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Роль</th>
                      <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Создан</th>
                      <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Статус</th>
                      <th className="text-center px-4 py-3 text-sm font-medium text-gray-600">Действия</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {adminUsers.map((admin) => (
                      <tr key={admin.id} className="hover:bg-gray-50" data-testid={`admin-row-${admin.id}`}>
                        <td className="px-4 py-3 font-medium">{admin.full_name || '—'}</td>
                        <td className="px-4 py-3 text-gray-600">{admin.email}</td>
                        <td className="px-4 py-3 text-center">
                          <Badge variant="outline">{admin.assigned_role_name || 'Super Admin'}</Badge>
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-500">
                          {formatDate(admin.created_at)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge className={admin.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                            {admin.is_active !== false ? 'Активен' : 'Неактивен'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Select
                            value={admin.assigned_role_id || 'super_admin'}
                            onValueChange={async (val) => {
                              try {
                                await axios.put(`${API}/admin/admin-users/${admin.id}/assign-role`, {
                                  role_id: val === 'super_admin' ? null : val
                                }, { headers: getAuthHeaders() });
                                toast.success('Роль назначена');
                                fetchAdminUsers();
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Ошибка');
                              }
                            }}
                          >
                            <SelectTrigger className="w-[160px] h-8 text-xs" data-testid={`assign-role-${admin.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="super_admin">Super Admin</SelectItem>
                              {roles.filter(r => r.name !== 'Super Admin').map((r) => (
                                <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audit Log Tab */}
          <TabsContent value="audit">
            {/* Filters */}
            <Card className="mb-4">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-4 items-end">
                  <div className="w-[180px]">
                    <Label className="text-sm text-gray-600">Действие</Label>
                    <Select 
                      value={auditFilters.action} 
                      onValueChange={(v) => setAuditFilters(prev => ({ ...prev, action: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Все действия</SelectItem>
                        {auditFilterOptions.actions.map(a => (
                          <SelectItem key={a} value={a}>{getActionLabel(a)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="w-[180px]">
                    <Label className="text-sm text-gray-600">Ресурс</Label>
                    <Select 
                      value={auditFilters.resource_type} 
                      onValueChange={(v) => setAuditFilters(prev => ({ ...prev, resource_type: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Все ресурсы</SelectItem>
                        {auditFilterOptions.resource_types.map(r => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="w-[150px]">
                    <Label className="text-sm text-gray-600">С даты</Label>
                    <Input
                      type="date"
                      value={auditFilters.date_from}
                      onChange={(e) => setAuditFilters(prev => ({ ...prev, date_from: e.target.value }))}
                    />
                  </div>

                  <div className="w-[150px]">
                    <Label className="text-sm text-gray-600">По дату</Label>
                    <Input
                      type="date"
                      value={auditFilters.date_to}
                      onChange={(e) => setAuditFilters(prev => ({ ...prev, date_to: e.target.value }))}
                    />
                  </div>

                  <Button variant="outline" size="sm" onClick={() => setAuditFilters({
                    action: 'all',
                    resource_type: 'all',
                    date_from: '',
                    date_to: ''
                  })}>
                    Сбросить
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Logs Table */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="audit-log-table">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Дата</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Пользователь</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Действие</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Ресурс</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Детали</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {auditLogs.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-4 py-12 text-center text-gray-500">
                            <History className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                            <p className="text-lg font-medium">Журнал пуст</p>
                            <p className="text-sm">Действия администраторов будут записаны здесь</p>
                          </td>
                        </tr>
                      ) : (
                        auditLogs.map((log) => (
                          <tr key={log.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {formatDate(log.created_at)}
                            </td>
                            <td className="px-4 py-3">
                              <span className="font-medium">{log.admin_email}</span>
                            </td>
                            <td className="px-4 py-3">
                              <Badge variant="outline">{getActionLabel(log.action)}</Badge>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-gray-600">{log.resource_type}</span>
                              {log.resource_id && (
                                <span className="text-gray-400 text-xs ml-1">({log.resource_id.slice(0, 8)}...)</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500 max-w-[200px] truncate">
                              {log.details ? JSON.stringify(log.details) : '—'}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Pagination */}
            {auditTotalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-gray-500">
                  Показано {auditLogs.length} из {auditTotal}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setAuditPage(p => Math.max(1, p - 1))}
                    disabled={auditPage === 1}
                  >
                    Назад
                  </Button>
                  <span className="px-3 py-1 text-sm">
                    {auditPage} из {auditTotalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setAuditPage(p => Math.min(auditTotalPages, p + 1))}
                    disabled={auditPage === auditTotalPages}
                  >
                    Далее
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Threats Tab */}
          <TabsContent value="threats" data-testid="threats-content">
            {/* Stats Cards */}
            {threatStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <Card className={threatStats.failed_logins > 0 ? "border-red-200 bg-red-50" : ""}>
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-500">Неудачные входы</p>
                    <p className="text-2xl font-bold text-red-600">{threatStats.failed_logins}</p>
                  </CardContent>
                </Card>
                <Card className={threatStats.rate_limits > 0 ? "border-orange-200 bg-orange-50" : ""}>
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-500">Rate Limit блокировки</p>
                    <p className="text-2xl font-bold text-orange-600">{threatStats.rate_limits}</p>
                  </CardContent>
                </Card>
                <Card className={threatStats.suspicious_requests > 0 ? "border-purple-200 bg-purple-50" : ""}>
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-500">Подозрительные</p>
                    <p className="text-2xl font-bold text-purple-600">{threatStats.suspicious_requests}</p>
                  </CardContent>
                </Card>
                <Card className="border-green-200 bg-green-50">
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-500">Успешные входы</p>
                    <p className="text-2xl font-bold text-green-600">{threatStats.successful_logins}</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Top Threat IPs */}
            {threatStats?.top_threat_ips?.length > 0 && (
              <Card className="mb-6 border-red-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Топ IP-адресов с подозрительной активностью</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {threatStats.top_threat_ips.map((item, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                        <span className="font-mono">{item.ip}</span>
                        <div className="flex items-center gap-4">
                          <Badge variant="destructive">{item.attempts} попыток</Badge>
                          <span className="text-gray-500 text-xs">{new Date(item.last_attempt).toLocaleString('ru')}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Filters */}
            <div className="flex items-center gap-3 mb-4">
              <Select value={threatFilter} onValueChange={(v) => { setThreatFilter(v); setThreatPage(1); }}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Тип события" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Все события</SelectItem>
                  <SelectItem value="failed_login">Неудачные входы</SelectItem>
                  <SelectItem value="rate_limit">Rate Limit</SelectItem>
                  <SelectItem value="suspicious">Подозрительные</SelectItem>
                  <SelectItem value="login_success">Успешные входы</SelectItem>
                </SelectContent>
              </Select>
              <Select value={String(threatDays)} onValueChange={(v) => { setThreatDays(Number(v)); setThreatPage(1); }}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Период" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 день</SelectItem>
                  <SelectItem value="7">7 дней</SelectItem>
                  <SelectItem value="30">30 дней</SelectItem>
                  <SelectItem value="90">90 дней</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={() => { fetchThreatStats(); fetchThreatLogs(); }}>
                <RefreshCw className="w-4 h-4 mr-1" /> Обновить
              </Button>
              <span className="text-sm text-gray-500 ml-auto">Всего: {threatTotal}</span>
            </div>

            {/* Logs Table */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="threat-log-table">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Время</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Тип</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">IP</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Email</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Детали</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Уровень</th>
                      </tr>
                    </thead>
                    <tbody>
                      {threatLoading ? (
                        <tr><td colSpan={6} className="text-center py-8 text-gray-500">Загрузка...</td></tr>
                      ) : threatLogs.length === 0 ? (
                        <tr><td colSpan={6} className="text-center py-8 text-gray-500">Нет событий за выбранный период</td></tr>
                      ) : threatLogs.map(log => (
                        <tr key={log.id} className={`border-b hover:bg-gray-50 ${log.severity === 'high' ? 'bg-red-50' : ''}`}>
                          <td className="px-4 py-3 text-sm whitespace-nowrap">{new Date(log.created_at).toLocaleString('ru')}</td>
                          <td className="px-4 py-3">
                            <Badge variant={
                              log.event_type === 'failed_login' ? 'destructive' :
                              log.event_type === 'rate_limit' ? 'secondary' :
                              log.event_type === 'suspicious' ? 'destructive' : 'default'
                            } className="text-xs">
                              {log.event_type === 'failed_login' ? 'Неудачный вход' :
                               log.event_type === 'rate_limit' ? 'Rate Limit' :
                               log.event_type === 'suspicious' ? 'Подозрительный' :
                               log.event_type === 'login_success' ? 'Вход' : log.event_type}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-sm font-mono">{log.ip_address}</td>
                          <td className="px-4 py-3 text-sm">{log.email || '—'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600 max-w-[200px] truncate">{log.details}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-block w-2 h-2 rounded-full ${
                              log.severity === 'high' ? 'bg-red-500' : 
                              log.severity === 'warning' ? 'bg-orange-500' : 'bg-green-500'
                            }`}></span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {threatTotal > 30 && (
                  <div className="flex justify-center gap-2 p-4 border-t">
                    <Button variant="outline" size="sm" disabled={threatPage <= 1} onClick={() => setThreatPage(p => p - 1)}>Назад</Button>
                    <span className="py-2 text-sm text-gray-500">Стр. {threatPage}</span>
                    <Button variant="outline" size="sm" disabled={threatLogs.length < 30} onClick={() => setThreatPage(p => p + 1)}>Вперёд</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Role Dialog */}
      <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRole ? 'Редактирование роли' : 'Новая роль'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSaveRole} className="space-y-4">
            <div>
              <Label>Название роли *</Label>
              <Input
                value={roleForm.name}
                onChange={(e) => setRoleForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Например: Менеджер продаж"
                required
              />
            </div>

            <div>
              <Label>Описание</Label>
              <Textarea
                value={roleForm.description}
                onChange={(e) => setRoleForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Краткое описание роли..."
                rows={2}
              />
            </div>

            <div>
              <Label className="mb-3 block">Права доступа</Label>
              <div className="space-y-4">
                {Object.entries(PERMISSION_GROUPS).map(([group, permissions]) => {
                  const groupSelected = permissions.filter(p => roleForm.permissions.includes(p)).length;
                  const allSelected = groupSelected === permissions.length;
                  
                  return (
                    <div key={group} className="p-3 border rounded-lg">
                      <div 
                        className="flex items-center gap-2 cursor-pointer mb-2"
                        onClick={() => toggleGroupPermissions(permissions)}
                      >
                        {allSelected ? (
                          <CheckSquare className="w-5 h-5 text-emerald-600" />
                        ) : groupSelected > 0 ? (
                          <div className="w-5 h-5 border-2 border-emerald-600 rounded bg-emerald-100" />
                        ) : (
                          <Square className="w-5 h-5 text-gray-400" />
                        )}
                        <span className="font-medium">{group}</span>
                        <span className="text-sm text-gray-500">({groupSelected}/{permissions.length})</span>
                      </div>
                      <div className="flex flex-wrap gap-2 ml-7">
                        {permissions.map((perm) => (
                          <div
                            key={perm}
                            className={`px-2 py-1 rounded text-sm cursor-pointer transition-colors ${
                              roleForm.permissions.includes(perm)
                                ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                            onClick={() => togglePermission(perm)}
                          >
                            {PERMISSION_LABELS[perm] || perm.split('.')[1]}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setShowRoleDialog(false)}>
                Отмена
              </Button>
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800">
                {editingRole ? 'Сохранить' : 'Создать'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Admin Dialog */}
      <Dialog open={showAdminDialog} onOpenChange={setShowAdminDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Новый администратор</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateAdmin} className="space-y-4">
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={newAdmin.email}
                onChange={(e) => setNewAdmin(prev => ({ ...prev, email: e.target.value }))}
                required
              />
            </div>

            <div>
              <Label>Пароль *</Label>
              <Input
                type="password"
                value={newAdmin.password}
                onChange={(e) => setNewAdmin(prev => ({ ...prev, password: e.target.value }))}
                required
              />
            </div>

            <div>
              <Label>Полное имя *</Label>
              <Input
                value={newAdmin.full_name}
                onChange={(e) => setNewAdmin(prev => ({ ...prev, full_name: e.target.value }))}
                required
              />
            </div>

            <div>
              <Label>Роль</Label>
              <Select
                value={newAdmin.role_id}
                onValueChange={(v) => setNewAdmin(prev => ({ ...prev, role_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Выберите роль" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAdminDialog(false)}>
                Отмена
              </Button>
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800">
                Создать
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminSecurity;
